import axios from "axios";
import { useState, useRef, useEffect } from "react";

// ── Icons ──
const BellIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9M13.73 21a2 2 0 01-3.46 0" />
  </svg>
);
const CloseIcon = () => (
  <svg viewBox="0 0 20 20" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M5 5l10 10M15 5L5 15" />
  </svg>
);
const DownloadIcon = () => (
  <svg viewBox="0 0 20 20" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <path d="M10 3v10M6 9l4 4 4-4M3 17h14" />
  </svg>
);
const CalendarIcon = () => (
  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <rect x="1.5" y="2.5" width="13" height="12" rx="1.5" />
    <path d="M5 1v3M11 1v3M1.5 7h13" />
  </svg>
);
const PinIcon = () => (
  <svg viewBox="0 0 16 16" width="12" height="12" fill="currentColor">
    <path d="M9.828.722a.5.5 0 01.354.146l4.95 4.95a.5.5 0 010 .707c-.48.48-1.072.588-1.503.588-.177 0-.335-.018-.46-.039l-3.134 3.134a5.927 5.927 0 01.16 1.013c.046.702-.032 1.687-.72 2.375a.5.5 0 01-.707 0l-2.829-2.828-3.182 3.182c-.195.195-1.219.902-1.414.707-.195-.195.512-1.22.707-1.414l3.182-3.182-2.828-2.829a.5.5 0 010-.707c.688-.688 1.673-.767 2.375-.72a5.922 5.922 0 011.013.16l3.134-3.133a2.772 2.772 0 01-.04-.461c0-.43.108-1.022.589-1.503a.5.5 0 01.353-.146z"/>
  </svg>
);
const EyeIcon = () => (
  <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M1 8s2.5-5 7-5 7 5 7 5-2.5 5-7 5-7-5-7-5z" />
    <circle cx="8" cy="8" r="2" />
  </svg>
);
const SearchIcon = () => (
  <svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <circle cx="9" cy="9" r="6" /><path d="M15 15l3 3" />
  </svg>
);
const ChevronIcon = ({ dir = "down" }) => (
  <svg viewBox="0 0 12 12" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    {dir === "down" ? <path d="M2 4l4 4 4-4" /> : <path d="M2 8l4-4 4 4" />}
  </svg>
);

// ── Data ──
const CATEGORIES = ["All", "General", "Tender", "Recruitment", "Policy", "Holiday", "Urgent"];

const CAT_STYLES = {
  General:     { bg: "#EAF3DE", text: "#3B6D11", dot: "#4ade80" },
  Tender:      { bg: "#ede9fe", text: "#5b21b6", dot: "#8b5cf6" },
  Recruitment: { bg: "#dbeafe", text: "#1e40af", dot: "#3b82f6" },
  Policy:      { bg: "#fce7f3", text: "#9d174d", dot: "#ec4899" },
  Holiday:     { bg: "#fef9c3", text: "#854d0e", dot: "#f59e0b" },
  Urgent:      { bg: "#fee2e2", text: "#991b1b", dot: "#ef4444" },
};

const NOTICES = [
  {
    id: 1,
    title: "Annual Solar Plant Maintenance Shutdown — Q2 2026",
    category: "General",
    date: "2026-04-01",
    expiry: "2026-04-15",
    pinned: true,
    views: 342,
    summary: "All residential and commercial solar installations will undergo scheduled preventive maintenance from April 10–14, 2026. Operations will resume on April 15.",
    body: `Dear Clients and Stakeholders,

This is to inform you that AG Services will conduct its **Q2 2026 Annual Preventive Maintenance Shutdown** for all solar installations under our management.

**Schedule:**
- Maintenance Period: April 10–14, 2026 (5 working days)
- Resumption of Full Operations: April 15, 2026

**What to expect:**
During this period, on-site technicians will inspect mounting structures, clean panels, check inverter performance, and audit electrical connections. Clients may experience brief monitoring data gaps.

**No action is required from your end.** Our team will contact you individually if access to your premises is needed.

For urgent assistance during this period, please call our helpline at **+91 8144915882**.`,
    attachment: "maintenance-schedule-q2-2026.pdf",
  },
  {
    id: 2,
    title: "Tender Notice: Supply of Solar PV Modules — 500 kWp",
    category: "Tender",
    date: "2026-03-28",
    expiry: "2026-04-20",
    pinned: true,
    views: 218,
    summary: "AG Services invites sealed bids from empanelled vendors for supply of 500 kWp Tier-1 solar PV modules for ongoing commercial projects in Odisha.",
    body: `**Reference No:** AGS/TND/2026/03

AG Services Pvt. Ltd. invites sealed bids from MNRE-approved manufacturers and distributors for the supply of **500 kWp (approx. 1,000 units × 500W) Tier-1 Monocrystalline PERC Solar PV Modules**.

**Tender Details:**
- Tender Value: ₹1.2 Crore (estimated)
- Bid Submission Deadline: April 20, 2026 by 5:00 PM IST
- Technical Bid Opening: April 22, 2026 at 11:00 AM
- Delivery Location: Bhubaneswar, Odisha

**Eligibility Criteria:**
- Minimum 3 years of supply experience
- Valid MNRE/BIS certification
- Annual turnover ≥ ₹2 Crore

Download the full tender document and submit bids to **procurement@agservices.com**.`,
    attachment: "tender-doc-pv-modules-2026.pdf",
  },
  {
    id: 3,
    title: "Walk-in Interview — Field Service Technicians (15 Vacancies)",
    category: "Recruitment",
    date: "2026-03-25",
    expiry: "2026-04-10",
    pinned: false,
    views: 589,
    summary: "AG Services is conducting walk-in interviews for 15 Field Service Technician positions across Odisha. ITI/Diploma holders with electrical background preferred.",
    body: `**AG Services Walk-in Interview Drive**

We are hiring **15 Field Service Technicians** to support our growing O&M (Operations & Maintenance) division across Odisha.

**Interview Details:**
- Date: April 5 & 6, 2026
- Time: 10:00 AM – 4:00 PM
- Venue: AG Services Office, Chandaka Industrial Estate, Patia, Bhubaneswar

**Eligibility:**
- ITI (Electrician/Electronic Mechanic) or Diploma in Electrical/Electronics
- Age: 18–35 years
- Basic knowledge of solar PV systems preferred
- Willingness to travel within Odisha

**Documents to Carry:**
- Updated resume (2 copies)
- All original certificates + 1 set of photocopies
- 2 passport-size photographs
- Valid government photo ID

Salary: ₹15,000–₹22,000/month + travel allowance.`,
    attachment: null,
  },
  {
    id: 4,
    title: "Revised Net Metering Policy — Effective April 1, 2026",
    category: "Policy",
    date: "2026-03-22",
    expiry: "2026-12-31",
    pinned: false,
    views: 412,
    summary: "OERC has revised the net metering policy for rooftop solar consumers in Odisha. Key changes include updated tariff rates and revised application process.",
    body: `Following the Odisha Electricity Regulatory Commission (OERC) order dated March 15, 2026, the **Revised Net Metering Policy** comes into effect from April 1, 2026.

**Key Changes:**
1. **Import/Export Tariff:** Net export units will now be credited at ₹2.80/unit (revised from ₹2.50/unit)
2. **Capacity Cap:** Individual rooftop systems may now be sized up to 100% of sanctioned load (increased from 90%)
3. **Application Process:** All new net metering applications to be submitted via the DISCOM online portal
4. **Bi-directional Metering:** DISCOM will provide and install bi-directional meters free of cost for systems above 5 kWp

**Action Required for Existing Clients:**
If your system was commissioned before March 2026, AG Services will contact you regarding documentation updates required for the new policy.

For queries, email **policy@agservices.com** or call us.`,
    attachment: "oerc-net-metering-policy-2026.pdf",
  },
  {
    id: 5,
    title: "Office Holiday Notice — Ram Navami & Good Friday",
    category: "Holiday",
    date: "2026-03-20",
    expiry: "2026-04-07",
    pinned: false,
    views: 156,
    summary: "AG Services offices will remain closed on April 2 (Good Friday) and April 6 (Ram Navami). Emergency support remains available 24×7.",
    body: `Dear Team and Clients,

Please note that AG Services offices across all locations will observe the following public holidays:

- **April 2, 2026 (Wednesday):** Good Friday
- **April 6, 2026 (Sunday):** Ram Navami

**Emergency Support:**
Our 24×7 helpline remains active during holidays for critical system failures:
📞 **+91 8144915882**
📧 **support@agservices.com**

Field service teams will be on standby for urgent O&M requirements. Prior booking recommended.

Wishing everyone a joyful holiday season! 🌟`,
    attachment: null,
  },
  {
    id: 6,
    title: "🚨 URGENT: Storm Advisory — Secure Solar Installations",
    category: "Urgent",
    date: "2026-03-18",
    expiry: "2026-03-22",
    pinned: false,
    views: 874,
    summary: "IMD has issued a yellow alert for coastal Odisha. All clients with rooftop solar installations are advised to take immediate precautionary measures.",
    body: `**India Meteorological Department (IMD) Yellow Alert — Coastal Odisha**

IMD has issued a weather advisory for strong winds (50–70 km/h) and heavy rain over coastal Odisha districts from March 19–21, 2026.

**Immediate Actions for Solar System Owners:**

✅ Do NOT attempt to access rooftop installations during the storm
✅ If your system has a manual disconnect, switch it off from the main panel
✅ Check that mounting bolts and clamps are secure (contact us if unsure)
✅ Keep inverters away from potential water ingress paths
✅ Do not touch any exposed wiring after the storm until inspected by a professional

**Post-Storm Inspection:**
AG Services will offer **free post-storm inspection** for all active clients within 7 days of the weather event. Contact us to schedule.

**24×7 Emergency:** +91 8144915882`,
    attachment: "storm-safety-checklist.pdf",
  },
  {
    id: 7,
    title: "New Office Address — Bhubaneswar Branch",
    category: "General",
    date: "2026-03-15",
    expiry: "2026-12-31",
    pinned: false,
    views: 203,
    summary: "Our Bhubaneswar branch has relocated to a new, larger facility to better serve our growing client base across Odisha.",
    body: `We are pleased to announce that AG Services' **Bhubaneswar Branch has relocated** to a new and expanded facility effective March 15, 2026.

**New Address:**
AG Services Pvt. Ltd.
Plot B/14, Near DLF Cyber City Road,
Chandaka Industrial Estate, Patia,
Bhubaneswar, Odisha — 751024

**Contact remains unchanged:**
📞 +91 8144915882
📧 info@agservicess.com

Our new space includes a dedicated **solar product showroom**, client meeting rooms, and an expanded technical training center. We invite all clients and partners to visit!

Direction maps and parking details are available on our website.`,
    attachment: null,
  },
  {
    id: 8,
    title: "Tender: Civil Works for Ground Mount Solar Farm — 2 MWp",
    category: "Tender",
    date: "2026-03-10",
    expiry: "2026-04-05",
    pinned: false,
    views: 134,
    summary: "Sealed tenders invited from civil contractors for earthwork, pile foundation, fencing, cable trenching for a 2 MWp ground mount solar project in Puri district.",
    body: `**Reference No:** AGS/TND/2026/02

AG Services invites sealed tenders from experienced civil contractors for the following scope at our **2 MWp Ground Mount Solar Project**, Puri District, Odisha:

**Scope of Work:**
- Land leveling and grading (5 acres)
- Pile foundation for mounting structures (approx. 800 nos.)
- Perimeter fencing (chain link, 2.5m height)
- Internal road construction (gravel, 500m)
- Cable trenching and backfilling
- Control room construction (prefab, 20 sqm)

**Tender Details:**
- EMD: ₹2,00,000 (DD in favour of AG Services Pvt. Ltd.)
- Submission Deadline: April 5, 2026 by 3:00 PM
- Site Visit: March 25, 2026 (mandatory)
- Project Duration: 45 days from LOI

Contact: **projects@agservices.com**`,
    attachment: "tender-civil-groundmount-2mwp.pdf",
  },
];

// ── Notice Detail Modal ──
function NoticeModal({ notice, onClose }) {
  const cat = CAT_STYLES[notice.category] || CAT_STYLES.General;
  const expDate = new Date(notice.expiry);
  const isExpired = expDate < new Date();

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      style={{ background: "rgba(0,0,0,0.55)", backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto no-scrollbar rounded-t-3xl sm:rounded-2xl bg-white"
        style={{ fontFamily: "'Barlow', sans-serif" }}
        onClick={e => e.stopPropagation()}
      >
        {/* Modal header */}
        <div
          className="sticky top-0 px-6 py-5 z-10"
          style={{ background: "linear-gradient(135deg, #0d2d0d, #1a4a1a)", borderRadius: "inherit inherit 0 0" }}
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2 flex-wrap">
                <span className="text-xs font-bold px-2.5 py-0.5 rounded-full"
                  style={{ background: cat.bg, color: cat.text }}>{notice.category}</span>
                {notice.pinned && (
                  <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                    style={{ background: "rgba(255,255,255,0.12)", color: "#fbbf24" }}>
                    <PinIcon /> Pinned
                  </span>
                )}
                
              </div>
              <h2 className="text-white font-black leading-snug" style={{ fontSize: "clamp(15px, 2vw, 19px)" }}>
                {notice.title}
              </h2>
              <div className="flex items-center gap-3 mt-2 flex-wrap">
                <span className="flex items-center gap-1.5 text-green-400 text-xs">
                  <CalendarIcon />
                  {new Date(notice.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </span>
                <span className="text-green-600 text-xs">·</span>
                <span className="flex items-center gap-1.5 text-green-400 text-xs">
                  <EyeIcon /> {notice.views} views
                </span>
                <span className="text-green-600 text-xs">·</span>
                <span className="text-green-400 text-xs">
                  Valid till: {expDate.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                </span>
              </div>
            </div>
            <button onClick={onClose} className="text-white/60 hover:text-white transition-colors mt-1 flex-shrink-0">
              <CloseIcon />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6">

          {/* Full body — render markdown-style bold */}
          <div className="text-gray-700 text-sm leading-relaxed space-y-3"
          dangerouslySetInnerHTML={{__html: notice.content}}
          />
            
         </div>
      </div>
    </div>
  );
}

// ── Notice Card ──
function NoticeCard({ notice, onClick, index }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef();
  const cat = CAT_STYLES[notice.category] || CAT_STYLES.General;
  const isExpired = new Date(notice.expiry) < new Date();

  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      onClick={() => onClick(notice)}
      className="group relative rounded-2xl cursor-pointer transition-all duration-300 hover:shadow-xl hover:-translate-y-0.5"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(28px)",
        transition: `opacity 0.5s ease ${index * 0.06}s, transform 0.5s ease ${index * 0.06}s, box-shadow 0.2s, translate 0.2s`,
        background: isExpired ? "#fafafa" : "white",
        border: notice.pinned
          ? "1.5px solid #4ade80"
          : `1.5px solid ${isExpired ? "#e5e7eb" : "#e8f0e0"}`,
        boxShadow: notice.pinned
          ? "0 4px 20px rgba(74,222,128,0.12)"
          : "0 2px 10px rgba(0,0,0,0.04)",
        fontFamily: "'Barlow', sans-serif",
        opacity: isExpired ? (visible ? 0.65 : 0) : (visible ? 1 : 0),
      }}
    >
      {/* Pinned indicator strip */}
      {notice.pinned && (
        <div className="absolute left-0 top-4 bottom-4 w-0.5 rounded-full"
          style={{ background: "linear-gradient(to bottom, #6ee85a, #3dcc40)", left: "-1px" }} />
      )}

      <div className="p-5">
        {/* Top row */}
        <div className="flex items-start justify-between gap-3 mb-3">
          <div className="flex items-center gap-2 flex-wrap flex-1">
            {/* Category */}
            <span className="flex items-center gap-1.5 text-xs font-bold px-2.5 py-0.5 rounded-full"
              style={{ background: cat.bg, color: cat.text }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ background: cat.dot }} />
              {notice.category}
            </span>
            {/* Pinned */}
            {notice.pinned && (
              <span className="flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full"
                style={{ background: "#fef9c3", color: "#92400e" }}>
                <PinIcon /> Pinned
              </span>
            )}
          </div>
          {/* Date */}
          <div className="flex items-center gap-1 text-gray-400 text-xs flex-shrink-0">
            <CalendarIcon />
            {new Date(notice.createdAt).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
          </div>
        </div>

        {/* Title */}
        <h3
          className="font-black text-gray-900 leading-snug mb-2 group-hover:text-green-700 transition-colors"
          style={{ fontSize: "clamp(14px, 1.5vw, 16px)" }}
        >
          {notice.title}
        </h3>

        <div className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-4"
        dangerouslySetInnerHTML={{__html: notice.content}}
        />

        {/* Bottom row */}
        <div className="flex items-center justify-between">
          <span
            className="text-xs font-bold flex items-center gap-1 transition-all group-hover:gap-2"
            style={{ color: "#3B6D11" }}
          >
            Read more
            <svg viewBox="0 0 12 12" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M2 6h8M7 3l3 3-3 3" />
            </svg>
          </span>
        </div>
      </div>
    </div>
  );
}

// ── Main Page ──
export default function NoticePage() {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");
  const [sortBy, setSortBy] = useState("newest");
  const [selectedNotice, setSelectedNotice] = useState(null);
  const [showExpired, setShowExpired] = useState(false);
  const [allNotice, setAllNotice] = useState([]);
  const [loading, setloading] = useState(false);

 const filtered = allNotice
  .filter(n => {
    const matchCat =
      activeCategory === "All" || n.category === activeCategory;

    const matchSearch =
      !search ||
      n.title.toLowerCase().includes(search.toLowerCase()) ||
      n.content.toLowerCase().includes(search.toLowerCase()) || // ✅ fixed
      (n.category || "").toLowerCase().includes(search.toLowerCase());

    return matchCat && matchSearch;
  })
  .sort((a, b) => {
    if (a.isPinned !== b.isPinned) return b.isPinned - a.isPinned; // ✅ fixed

    if (sortBy === "newest")
      return new Date(b.createdAt) - new Date(a.createdAt); // ✅ fixed

    if (sortBy === "oldest")
      return new Date(a.createdAt) - new Date(b.createdAt);

    return 0;
  });

  const pinned = filtered.filter(n => n.isPinned);
  const regular = filtered.filter(n => !n.isPinned);

  const getAllNotice = async()=>{
    try {
      setloading(true);
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/notice/getAll`);
      setAllNotice(res.data.data || []);
    } catch (error) {
      console.log("Error", error);
    }finally{
      setloading(false);
    }
  }

  useEffect(()=>{
    getAllNotice();
  },[]);


  if (loading) {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <p className="text-gray-500 font-semibold animate-pulse">
        Loading notices...
      </p>
    </div>
  );
}

  return (
    <div className="min-h-screen" style={{ background: "#f8faf6", fontFamily: "'Barlow', sans-serif" }}>

      {/* ── Controls ── */}
      <div className="px-6 md:px-10 lg:px-12 xl:px-20 py-8">

        {/* Search */}
       

        {/* Filters row */}
        <div className="flex flex-col sm:flex-row md:items-center gap-4 mb-6">

           <div className="relative mb-5 flex-1">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"><SearchIcon /></span>
          <input
            value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search notices by title, category, or keyword..."
            className="w-full xl:max-w-5xl pl-11 pr-4 py-3.5 text-sm rounded-2xl border outline-none transition-all border-[#e0ead8] bg-white "
            onFocus={e => e.target.style.borderColor = "#4ade80"}
            onBlur={e => e.target.style.borderColor = "#e0ead8"}
          />
        </div>

          {/* Category tabs */}
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map(cat => (
              <button key={cat} onClick={() => setActiveCategory(cat)}
                className="text-xs font-bold px-3.5 py-2 rounded-xl transition-all"
                style={{
                  background: activeCategory === cat
                    ? (cat === "All" ? "linear-gradient(135deg, #6ee85a, #3dcc40)" : CAT_STYLES[cat]?.bg || "#EAF3DE")
                    : "white",
                  color: activeCategory === cat
                    ? (cat === "All" ? "#0d2d0d" : CAT_STYLES[cat]?.text || "#3B6D11")
                    : "#6b7280",
                  border: activeCategory === cat ? "none" : "1px solid #e5e7eb",
                  boxShadow: activeCategory === cat ? "0 2px 8px rgba(0,0,0,0.08)" : "none",
                }}>
                {cat}
              </button>
            ))}
          </div>

        </div>



        {/* Result count */}
        <p className="text-sm text-gray-500 mb-6">
          Showing <strong className="text-gray-800">{filtered.length}</strong> notice{filtered.length !== 1 ? "s" : ""}
          {activeCategory !== "All" && ` in ${activeCategory}`}
          {search && ` matching "${search}"`}
        </p>

        {/* ── Pinned notices ── */}
        {pinned.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center gap-2 mb-4">
              <span style={{ color: "#3B6D11" }}><PinIcon /></span>
              <h2 className="text-sm font-black text-gray-700 uppercase tracking-widest">Pinned</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {pinned.map((n, i) => (
                <NoticeCard key={n._id} notice={n} onClick={setSelectedNotice} index={i} />
              ))}
            </div>
          </div>
        )}

        {/* ── All notices ── */}
        {regular.length > 0 && (
          <div>
            {pinned.length > 0 && (
              <div className="flex items-center gap-3 mb-4">
                <h2 className="text-sm font-black text-gray-700 uppercase tracking-widest">All Notices</h2>
                <div className="flex-1 h-px" style={{ background: "#e0ead8" }} />
              </div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {regular.map((n, i) => (
                <NoticeCard key={n.id} notice={n} onClick={setSelectedNotice} index={i + pinned.length} />
              ))}
            </div>
          </div>
        )}

        {/* Empty state */}
        {filtered.length === 0 && (
          <div className="text-center py-24 text-gray-400">
            <p className="text-5xl mb-4">📋</p>
            <p className="font-black text-gray-600 text-lg mb-2">No notices found</p>
            <p className="text-sm">Try adjusting your search or filters.</p>
            <button
              onClick={() => { setSearch(""); setActiveCategory("All"); setShowExpired(false); }}
              className="mt-5 text-sm font-bold px-5 py-2.5 rounded-xl transition-all"
              style={{ background: "linear-gradient(135deg, #6ee85a, #3dcc40)", color: "#0d2d0d" }}
            >
              Reset filters
            </button>
          </div>
        )}
      </div>

      {/* Notice detail modal */}
      {selectedNotice && (
        <NoticeModal notice={selectedNotice} onClose={() => setSelectedNotice(null)} />
      )}
    </div>
  );
}